/**
 * The lazily loaded pages, by the URL prefix that reaches them — so a chunk
 * can be fetched BEFORE the navigation that needs it.
 *
 * Route-level code splitting (App.js, 2026-09-07) cut the main script by a
 * third, at the price of one round trip on the first visit to a lazy page.
 * This hides it: when a reader hovers, focuses or touches a link to one of
 * these pages, its chunk is requested then; by the time they click, it is
 * usually in hand. Each thunk imports the SAME module App.js's `lazy()` does,
 * so webpack emits one chunk and the prefetch simply warms it.
 *
 * Prefixes match on whole path segments and the longest one wins, so
 * `/authoring/homebrew/3` warms the layer editor's page and `/authoring/x`
 * the system editor's. A `:name` segment matches any one segment (the sheet
 * builder's address has a slug and a key in it); the prefix with the most
 * segments wins. The reference pages are not here: they are in the main
 * chunk and need no warming.
 */

export const ROUTE_CHUNKS = {
  '/favorites': () => import('../pages/Favorites'),
  '/dm-screen': () => import('../pages/DmScreen'),
  '/characters': () => import('../pages/Characters'),
  '/campaigns': () => import('../pages/Campaigns'),
  '/wiki': () => import('../pages/CampaignWikiReader'),
  '/table': () => import('../pages/TableHandoff'),
  '/test-login': () => import('../pages/TestLogin'),
  '/account': () => import('../pages/Account'),
  '/privacy': () => import('../pages/Privacy'),
  '/terms': () => import('../pages/Terms'),
  '/ogl': () => import('../pages/OGL'),
  '/licenses': () => import('../pages/OGL'),
  '/shop': () => import('../pages/ShopGenerator'),
  '/treasure': () => import('../pages/TreasureGenerator'),
  '/trap-builder': () => import('../pages/TrapBuilder'),
  '/tracker': () => import('../pages/InitiativeTracker'),
  '/tracker/shared': () => import('../pages/SharedTracker'),
  '/guide': () => import('../pages/FeaturesGuide'),
  '/systems-guide': () => import('../pages/SystemsGuide'),
  '/share': () => import('../pages/ClaimShare'),
  '/admin/reports': () => import('../pages/AdminReports'),
  '/admin/content': () => import('../pages/AdminContent'),
  '/admin/verification': () => import('../pages/AdminVerification'),
  '/authoring': () => import('../pages/authoring/MySystems'),
  '/authoring/shared': () => import('../pages/authoring/SharedWithMe'),
  '/authoring/homebrew': () => import('../pages/authoring/MyHomebrew'),
  '/authoring/workbench': () => import('../pages/authoring/workbench/Workbench'),
  '/authoring/:slug/sheet/:key/build': () => import('../pages/authoring/SheetBuilder'),
  '/authoring/homebrew/:id/sheet/:key/build': () => import('../pages/authoring/SheetBuilder'),
};

const segments = (p) => p.split('/').filter(Boolean);
/** Does this prefix match the start of the path, segment by segment? */
function prefixMatches(prefix, parts) {
  const want = segments(prefix);
  return want.length <= parts.length && want.every((w, i) => (w.startsWith(':') ? parts[i] !== '' : w === parts[i]));
}

/** The thunk for a path, by longest whole-segment prefix, or null. */
export function chunkFor(pathname) {
  if (typeof pathname !== 'string' || !pathname.startsWith('/')) return null;
  const path = pathname.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  const parts = segments(path);
  let best = null;
  for (const prefix of Object.keys(ROUTE_CHUNKS)) {
    if (prefixMatches(prefix, parts) && (!best || segments(prefix).length > segments(best).length)) best = prefix;
  }
  return best ? ROUTE_CHUNKS[best] : null;
}

const started = new Set();

/** Warm the chunk for a path once; a failure is nothing (the navigation retries). */
export function prefetchRoute(pathname) {
  const thunk = chunkFor(pathname);
  if (!thunk || started.has(thunk)) return false;
  started.add(thunk);
  Promise.resolve().then(thunk).catch(() => { started.delete(thunk); });
  return true;
}

/**
 * One delegated handler for a whole subtree: any same-origin link hovered,
 * focused or touched warms its page. Mount it on the layout's root.
 */
export function prefetchFromEvent(event) {
  const target = event && event.target;
  if (!target || typeof target.closest !== 'function') return;
  const a = target.closest('a[href]');
  if (!a) return;
  const href = a.getAttribute('href') || '';
  if (!href.startsWith('/')) return;
  prefetchRoute(href);
}

/** For tests. */
export function _resetPrefetch() { started.clear(); }
